import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Clock, Star, Building, Users, Briefcase, ChevronDown, Sparkles } from 'lucide-react';
import { useContent } from '../context/ContentContext';

const Programs = () => {
  const { programsContent } = useContent();
  const [activeTab, setActiveTab] = useState('individuals');
  const [expandedProgram, setExpandedProgram] = useState(null);

  const tabs = [
    { id: 'individuals', label: 'For Individuals', icon: Users, color: 'var(--accent-gold)' },
    { id: 'businesses', label: 'For Businesses & Teams', icon: Building, color: 'var(--accent-blue)' },
  ];

  const ProgramCard = ({ program, index, category }) => {
    const isExpanded = expandedProgram === `${category}-${index}`;
    
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: index * 0.1 }}
        className="relative group"
        data-testid="program-card-item"
      >
        <motion.div
          animate={{ scale: isExpanded ? 1.02 : 1 }}
          className={`interactive-card p-6 cursor-pointer shimmer-border ${
            isExpanded ? 'border-[var(--accent-teal)]' : ''
          }`}
          onClick={() => setExpandedProgram(isExpanded ? null : `${category}-${index}`)}
        >
          {/* Highlight Badge */}
          {program.highlight && (
            <motion.span 
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="absolute -top-3 right-4 badge-highlight flex items-center gap-1"
            >
              <Sparkles className="w-3 h-3" />
              {program.highlight}
            </motion.span>
          )}

          <div className="flex justify-between items-start mb-4">
            <div className="flex-1 pr-4">
              <h4 className="font-display text-lg font-semibold text-[var(--text-primary)] group-hover:text-[var(--accent-teal)] transition-colors">
                {program.name}
              </h4>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 text-sm text-[var(--accent-gold)] bg-[var(--accent-gold)]/10 px-3 py-1 rounded-full">
                <Clock className="w-3.5 h-3.5" />
                {program.duration}
              </div>
              <motion.div
                animate={{ rotate: isExpanded ? 180 : 0 }}
                className="w-6 h-6 rounded-full bg-white/5 flex items-center justify-center"
              >
                <ChevronDown className="w-4 h-4 text-[var(--text-muted)]" />
              </motion.div>
            </div>
          </div>

          <p className="text-[var(--text-muted)] text-sm leading-relaxed">
            {program.description}
          </p>

          {program.subDescription && (
            <p className="text-[var(--text-muted)] text-xs leading-relaxed mt-2 opacity-70">
              {program.subDescription}
            </p>
          )}

          {/* Expanded Content */}
          <AnimatePresence>
            {isExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <div className="pt-4 mt-4 border-t border-white/10">
                  <div className="flex flex-wrap gap-2 mb-4">
                    {['Live Sessions', '1-on-1 Coaching', 'Practice Labs', 'Resources'].map((feature) => (
                      <span key={feature} className="text-xs px-3 py-1 rounded-full bg-[var(--accent-teal)]/10 text-[var(--accent-teal)]">
                        {feature}
                      </span>
                    ))}
                  </div>
                  <a 
                    href="#contact-form"
                    className="inline-flex items-center gap-2 text-sm font-medium text-[var(--accent-teal)] hover:text-[var(--accent-gold)] transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Inquire About This Program
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Bottom gradient line */}
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: isExpanded ? 1 : 0 }}
            className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[var(--accent-teal)] to-[var(--accent-gold)] origin-left"
          />
        </motion.div>
      </motion.div>
    );
  };

  return (
    <section id="programs" className="py-24 bg-pattern relative overflow-hidden" data-testid="programs-section">
      {/* Background decoration */}
      <div className="absolute inset-0 pointer-events-none">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 100, repeat: Infinity, ease: "linear" }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[1000px] rounded-full border border-white/[0.02]"
        />
      </div>

      <div className="container-main relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <motion.div
            initial={{ scale: 0 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            transition={{ type: "spring", stiffness: 200 }}
            className="inline-flex items-center gap-2 mb-4 px-4 py-2 rounded-full border border-[var(--accent-teal)]/30 bg-[var(--accent-teal)]/5"
          >
            <Briefcase className="w-4 h-4 text-[var(--accent-teal)]" />
            <span className="text-sm font-medium text-[var(--accent-teal)]">Transformative Programs</span>
          </motion.div>
          
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[var(--text-primary)] mb-4">
            <span className="gradient-text">Magnetic Influential</span> Programs
          </h2>
          <p className="text-base md:text-lg text-[var(--text-muted)] max-w-2xl mx-auto px-4">
            {programsContent.subheadline}
          </p>
        </motion.div>

        {/* Tab Switcher */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex justify-center mb-12"
        >
          <div className="inline-flex p-1.5 rounded-2xl bg-white/[0.03] border border-white/10">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`relative flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-medium transition-all duration-300 ${
                  activeTab === tab.id
                    ? 'text-white'
                    : 'text-[var(--text-muted)] hover:text-[var(--text-primary)]'
                }`}
              >
                {activeTab === tab.id && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute inset-0 rounded-xl bg-gradient-to-r from-[var(--accent-teal)] to-[var(--accent-blue)]"
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  />
                )}
                <tab.icon className="w-4 h-4 relative z-10" />
                <span className="relative z-10">{tab.label}</span>
              </button>
            ))}
          </div>
        </motion.div>

        {/* Programs Grid */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, x: activeTab === 'individuals' ? -20 : 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: activeTab === 'individuals' ? 20 : -20 }}
            transition={{ duration: 0.3 }}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto"
          >
            {(activeTab === 'individuals' 
              ? programsContent.individuals.programs 
              : programsContent.businesses.programs
            ).map((program, index) => (
              <ProgramCard 
                key={program.name} 
                program={program} 
                index={index}
                category={activeTab}
              />
            ))}
          </motion.div>
        </AnimatePresence>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="text-center mt-12"
        >
          <p className="text-[var(--text-muted)] mb-4">
            Not sure which program is right for you?
          </p>
          <a 
            href="#contact-form"
            className="inline-flex items-center gap-2 text-[var(--accent-teal)] hover:text-[var(--accent-gold)] font-medium transition-colors"
          >
            Let's discuss your goals
            <ArrowRight className="w-4 h-4" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Programs;
